/**
 * DashboardHeader Component
 *
 * Enhanced branded header for the Stock Sentry dashboard with:
 * - Stock Sentry logo and brand name
 * - StatCard components with backdrop badges and trends
 * - Hover effects and smooth transitions
 * - Responsive layout with proper wrapping
 *
 * Design features badge images as backdrop overlays with semantic coloring
 */

import type { RefObject } from 'react';
import { StockSentryLogo } from '../ui/StockSentryLogo';
import { RetailForgeLogo } from '../ui/RetailForgeLogo';
import { StatCard } from './StatCard';
import type { TrendData } from './TrendIndicator';
import type { StatCardType } from './StatCardActions';
import type { DashboardStatistics } from '../../types/dashboard';

/**
 * Stat configuration for cards
 */
interface StatConfig {
  id: StatCardType;
  label: string;
  getValue: (stats: DashboardStatistics) => number;
  badgeImage: string;
  tabIndex: number;
}

const STAT_CONFIGS: StatConfig[] = [
  {
    id: 'monitored',
    label: 'Monitored',
    getValue: (stats) => stats.productsMonitored,
    badgeImage: '/images/monitored-winner.png',
    tabIndex: 1,
  },
  {
    id: 'critical',
    label: 'Critical',
    getValue: (stats) => stats.criticalAlerts,
    badgeImage: '/images/critical-winner.png',
    tabIndex: 0,
  },
  {
    id: 'warning',
    label: 'Warning',
    getValue: (stats) => stats.warningAlerts,
    badgeImage: '/images/warning-winner.png',
    tabIndex: 0,
  },
  {
    id: 'healthy',
    label: 'Healthy',
    getValue: (stats) => stats.healthyStock,
    badgeImage: '/images/healthy-winner.png',
    tabIndex: 1,
  },
];

/**
 * Trend data for all stat types
 */
export interface StatTrends {
  monitored?: TrendData;
  critical?: TrendData;
  warning?: TrendData;
  healthy?: TrendData;
}

export interface DashboardHeaderProps {
  /** Statistics to display in cards */
  statistics: DashboardStatistics;
  /** Optional trend data for period comparisons */
  trends?: StatTrends;
  /** Callback when a stat card is clicked */
  onStatClick?: (tabIndex: number, ref?: RefObject<HTMLDivElement>) => void;
  /** Optional ref to scroll to when clicking stats */
  scrollRef?: RefObject<HTMLDivElement>;
}

/**
 * DashboardHeader - Enhanced branded header with logo and stat cards
 */
export function DashboardHeader({
  statistics,
  trends,
  onStatClick,
  scrollRef,
}: DashboardHeaderProps) {
  const handleCardClick = (tabIndex: number) => {
    onStatClick?.(tabIndex, scrollRef);
  };

  return (
    <div className="dashboard-header-card">
      <div className="dashboard-header-content">
        {/* Brand identity section */}
        <div className="dashboard-header-logo-section">
          <div className="dashboard-header-logo-icon">
            <StockSentryLogo size={28} color="white" />
          </div>
          <div className="dashboard-header-brand">
            <span className="dashboard-header-logo-text">StockSentry</span>
            <a
              href="https://retailforge.app"
              target="_blank"
              rel="noopener noreferrer"
              className="dashboard-header-subtext"
            >
              By RetailForge <RetailForgeLogo size={14} />
            </a>
          </div>
        </div>

        {/* Stat cards container */}
        <div className="dashboard-header-badges">
          {STAT_CONFIGS.map((stat) => (
            <StatCard
              key={stat.id}
              id={stat.id}
              label={stat.label}
              value={stat.getValue(statistics)}
              badgeImage={stat.badgeImage}
              trend={trends?.[stat.id]}
              tabIndex={stat.tabIndex}
              onClick={handleCardClick}
              scrollRef={scrollRef}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
